import Head from 'next/head';
import Link from 'next/link';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  buttonSecondary,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';

import { COOKIE_DAYS, REWARDFUL } from './kit';

type Faq = {
  question: string;
  answer: string;
  link?: { href: string; label: string };
};

const FAQS: Faq[] = [
  {
    question: 'How much do I earn?',
    answer:
      '30% of every payment from the paying customers you refer, every month they pay. Yearly plans earn 30% of the yearly payment.',
  },
  {
    question: 'What is the minimum payout threshold?',
    answer:
      'The minimum payout is US$50. If your commission balance is below US$50, it carries forward to the following month and keeps accumulating until you reach the threshold.',
  },
  {
    question: 'When and how are commissions paid?',
    answer:
      'Commissions are paid to your PayPal account in the first week of each month, provided your commission balance has reached US$50. You can follow your clicks, referrals and commissions in the affiliate dashboard.',
  },
  {
    question: 'Which payout methods do you support?',
    answer: 'PayPal is currently the only supported payout method.',
  },
  {
    question: 'How long does a referral count?',
    answer: `${COOKIE_DAYS} days. If someone clicks your link and buys within ${COOKIE_DAYS} days of their most recent click, the sale is credited to you. After that the cookie expires.`,
  },
  {
    question: 'Do I need to be an Easy-Peasy.AI customer?',
    answer:
      'No. The most successful affiliates often use the product themselves, but it isn’t required. Everyone is welcome to join.',
  },
  {
    question: 'Is it free to join?',
    answer: 'Yes. Signing up costs nothing.',
  },
  {
    question: 'Can I sign up through my own link?',
    answer:
      'No. Self-referrals aren’t allowed: the program rewards bringing Easy-Peasy.AI to new people.',
  },
  {
    question: 'Can I run search ads on non-branded keywords?',
    answer:
      'Yes. You can run paid search campaigns on generic, non-branded keywords such as “AI writing tool” without prior approval. Add “Easy-Peasy.AI” and “Easy Peasy AI” as negative keywords, and make sure your ads don’t imply that you represent us.',
  },
  {
    question: 'Which paid ads are prohibited?',
    answer:
      'Don’t bid on our brand names or domain names, and don’t run Facebook ads that link to our website. Ads must not mislead customers or suggest you are part of our team. Sales from prohibited ads don’t earn commissions, and your affiliate account may be closed.',
  },
  {
    question: 'Can I request an exclusive coupon code for my audience?',
    answer:
      'Yes. Use our Contact Us form to request an exclusive coupon code. Include your affiliate account email and a brief description of your audience and how you plan to promote Easy-Peasy.AI.',
    link: { href: '/contact', label: 'Request a coupon code' },
  },
  {
    question: 'A customer signed up without my link. Can I still get credit?',
    answer:
      'Sometimes. Contact us with the details and we’ll do our best to help.',
  },
];

export default function AffiliatesFaq() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };

  return (
    <section id='faq' className='pb-24 sm:pb-32'>
      <Head>
        <script
          type='application/ld+json'
          // Static copy from this file: nothing user-supplied goes in here.
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData).replace(/</g, '\\u003c'),
          }}
        />
      </Head>
      <div className='mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-8'>
        <div className='lg:col-span-4'>
          <SectionHeading
            size='md'
            title='Questions, answered.'
            lead='Already an affiliate? Your links, referrals and payouts are in your dashboard.'
          />
          <a href={REWARDFUL.login} className={clsxm(buttonSecondary, 'mt-8')}>
            Log in to your dashboard
            <ArrowGlyph className='h-3.5 w-3.5' />
          </a>
        </div>

        <ul className='divide-y divide-[#0f0f28]/[0.08] border-y border-[#0f0f28]/[0.08] lg:col-span-8 dark:divide-white/[0.08] dark:border-white/[0.08]'>
          {FAQS.map(({ question, answer, link }) => (
            <li key={question}>
              <details className='group'>
                <summary
                  className={clsxm(
                    'flex cursor-pointer list-none items-center justify-between gap-6 py-6 text-start text-lg font-semibold transition-colors hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600 dark:hover:text-primary-300 [&::-webkit-details-marker]:hidden',
                    tone.ink,
                  )}
                >
                  {question}
                  <span
                    aria-hidden='true'
                    className={clsxm(
                      'relative flex h-8 w-8 flex-none items-center justify-center rounded-full',
                      tone.line,
                    )}
                  >
                    <span className='absolute h-px w-3 bg-current' />
                    <span className='absolute h-3 w-px bg-current transition-transform duration-300 group-open:rotate-90 group-open:opacity-0' />
                  </span>
                </summary>
                <p
                  className={clsxm(
                    'max-w-2xl pb-7 pe-12 text-[16px] leading-7',
                    tone.body,
                  )}
                >
                  {answer}
                  {link ? (
                    <>
                      {' '}
                      <Link
                        href={link.href}
                        className='font-medium text-primary-700 underline underline-offset-4 hover:text-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600 dark:text-primary-300 dark:hover:text-primary-200'
                      >
                        {link.label}
                      </Link>
                    </>
                  ) : null}
                </p>
              </details>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
